import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { RouteCodeEnum } from '@models/menu';
import { SystemModulesEnum } from '@models/module/main/systemModules.enum';

import { DocumentDetailsDialogData, DocumentInfoModel } from '@module-instance/dynamic-module/document-details/common';
import { DocumentDetailsDialogComponent } from '@module-instance/dynamic-module/document-details/document-details-dialog/document-details-dialog.component';
import { ProgressPaymentService } from '@module-instance/services';
import { NgToJsService } from '@ng2/services/ng-to-js.service';
import { UserStore } from '@signal-store';
import { NavigationService } from './navigation.service';
import { UserService } from './user.service';

/** This type contains all the properties required for opening a document (both navigate to hybrid and open dialog) */
export type GeneralDocumentData = Omit<DocumentDetailsDialogData, 'workflowId' | 'screenId'> & {
  paymentPeriodId?: number;
  /** if undefined, will be retrieved from menu */
  workflowId?: number;
  /** if undefined, will be retrieved from GeneralInformation. Required for new documents */
  screenId?: number;
};

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private userStore = inject(UserStore);
  private dialog = inject(MatDialog);
  private ngToJsService = inject(NgToJsService);
  private ppService = inject(ProgressPaymentService);
  private navigation = inject(NavigationService);
  private userService = inject(UserService);

  /** Opens a document either by navigating to a hybrid route or opening a dialog
   * @param document - The document data containing all necessary properties
   * @returns void or matDialogRef depending on the action taken
   */
  openDocument(document: GeneralDocumentData) {
    const moduleExists = this.userStore.menuItemsList().find(menuItem => menuItem.moduleId === document.moduleId);
    if (!moduleExists) {
      return;
    }
    const { contractId, entityInstanceId, paymentPeriodId, moduleId } = document;
    switch (moduleId) {
      case SystemModulesEnum.ProgressPayment:
        if (!paymentPeriodId) {
          this.ppService
            .getPpPeriod(entityInstanceId)
            .subscribe(({ id }) =>
              this.navigation
                .tenantNavigate(['/progresspayment', contractId, id, entityInstanceId])
                .then(() => this.afterHybridDocumentNavigate(RouteCodeEnum.ProgressPayment, contractId)),
            );
          return;
        } else {
          this.navigation
            .tenantNavigate(['/progresspayment', contractId, paymentPeriodId, entityInstanceId])
            .then(() => this.afterHybridDocumentNavigate(RouteCodeEnum.ProgressPayment, contractId));
          return;
        }
      case SystemModulesEnum.SOV:
        this.navigation
          .tenantNavigate(['/sov', contractId, entityInstanceId])
          .then(() => this.afterHybridDocumentNavigate(RouteCodeEnum.SOV, contractId));
        return;
      case SystemModulesEnum.SOVI:
        return;
      default:
        return this.openDocumentDetailsDialog(document);
    }
  }

  /** Opens a document dialog
   * @param document - The document data containing all necessary properties
   * @returns matDialogRef<DocumentDetailsDialogComponent>
   */
  openDocumentDetailsDialog({
    contractId,
    entityInstanceId,
    moduleId,
    urlActionId,
    screenId,
    workflowId,
    showSidebar,
    templateId,
    activityInstanceId,
    openInEditMode,
  }: GeneralDocumentData) {
    if (!workflowId) {
      workflowId = this.userStore.menuItemsList().find(mi => mi.moduleId === moduleId)?.workflowId;
    }
    return this.dialog.open<DocumentDetailsDialogComponent, DocumentDetailsDialogData, Partial<DocumentInfoModel>>(
      DocumentDetailsDialogComponent,
      {
        panelClass: ['cipo', 'cipo-dialog', 'classic', 'cipo--height--full'],
        ...this.userService.getResponsiveDialogSize().xl,
        data: {
          moduleId,
          contractId,
          screenId,
          workflowId,
          templateId,
          urlActionId,
          entityInstanceId,
          activityInstanceId,
          showSidebar,
          ...(openInEditMode ? { openInEditMode: true } : {}),
        },
      },
    );
  }

  private closeDialogInstances() {
    this.ngToJsService.closeAllDialogs();
    this.dialog.openDialogs.forEach(dialogRef => dialogRef.close({ skipAfterClose: true }));
  }

  /**
   * After navigating to a hybrid document URL, sync contract context when needed.
   * When the contract changes, skip setActiveMenuByCode — the menu reload updates urlPath
   * and setActiveMenuByUrl selects the active item without redirecting away from the detail URL.
   */
  private afterHybridDocumentNavigate(menuCode: RouteCodeEnum, contractId?: number) {
    this.closeDialogInstances();
    const contractSyncNeeded = this.isContractSyncNeeded(contractId);
    this.syncContractContextIfNeeded(contractId);
    if (!contractSyncNeeded) {
      this.userStore.setActiveMenuByCode(menuCode, false);
    }
  }

  private isContractSyncNeeded(contractId?: number): boolean {
    if (contractId == null || Number.isNaN(Number(contractId))) {
      return false;
    }
    return Number(this.userStore.contract()?.id) !== Number(contractId);
  }

  private syncContractContextIfNeeded(contractId?: number) {
    if (contractId == null || Number.isNaN(Number(contractId))) {
      return;
    }
    const currentId = this.userStore.contract()?.id;
    if (Number(currentId) !== Number(contractId)) {
      this.userStore.updateContextByContractId(contractId);
    }
  }
}
